import { Heart, ScanSearch } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/page-header';
import { ErrorState } from '@/components/common/states';
import { NewJobDialog } from '@/components/indexing/new-job-dialog';
import { EditionFilter, parseEdition } from '@/components/movie/edition-filter';
import { MovieGrid } from '@/components/movie/movie-grid';
import { PersonPhoto } from '@/components/movie/person-photo';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useFilterParams } from '@/hooks/use-filter-params';
import { useFavouritePerson, usePerson } from '@/hooks/use-movies';
import { cn } from '@/lib/utils';

export function PersonPage() {
  const personId = Number(useParams().personId);
  const person = usePerson(personId);
  const favourite = useFavouritePerson(personId);
  const [indexing, setIndexing] = useState(false);
  const { params, set } = useFilterParams();
  const edition = parseEdition(params.get('edition'));
  const solo = params.get('solo') === '1';
  if (person.isError) {
    return <ErrorState error={person.error} />;
  }
  const found = person.data?.person;
  // Her page on a source, as a category to index; the first source that lists her.
  const category = found?.categories?.[0];
  const isFavourite = found?.favourite === true;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        {found === undefined ? null : <PersonPhoto name={found.name} photo={found.photo} className="size-20 text-2xl" />}
        <PageHeader
          className="min-w-0 flex-1"
          title={found?.name ?? '…'}
          description={
            found === undefined
              ? undefined
              : `${found.movieCount.toLocaleString()} in the library${category?.movieCount === undefined ? '' : ` · ${category.movieCount.toLocaleString()} on ${category.sourceId}`}`
          }
          actions={
            found === undefined ? undefined : (
              <>
                <Button variant="outline" onClick={() => favourite.mutate(!isFavourite)} disabled={favourite.isPending} aria-pressed={isFavourite}>
                  <Heart className={cn(isFavourite && 'fill-primary text-primary')} />
                  Favourite
                </Button>
                <Button onClick={() => setIndexing(true)} disabled={category === undefined} title={category === undefined ? undefined : `Index her movies from ${category.sourceId}`}>
                  <ScanSearch />
                  Index her movies
                </Button>
              </>
            )
          }
        />
      </div>
      {found !== undefined && category === undefined ? (
        <p className="text-sm text-muted-foreground">No source lists a page of hers to index yet. Reading the categories (under Indexing) finds it if the source has one.</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <EditionFilter value={edition} onChange={(value) => set('edition', value)} />
        <div className="flex items-center gap-2" title="Only movies with no one else in the cast">
          <Switch id="solo" checked={solo} onCheckedChange={(checked) => set('solo', checked ? '1' : undefined)} />
          <Label htmlFor="solo">Solo only</Label>
        </div>
      </div>
      <MovieGrid
        request={{ personId, edition, solo: solo ? true : undefined }}
        empty={{ title: edition === undefined && !solo ? 'No movies indexed with this person yet' : 'Nothing matches these filters' }}
      />
      <NewJobDialog open={indexing} onOpenChange={setIndexing} category={category} />
    </div>
  );
}
